import { useEffect, useRef, useState } from 'react';
import { flexRender, type ColumnDef, type Table, type Row } from '@tanstack/react-table';
import { DEFAULT_IMAGE_FORMAT_ID } from '@gorilla/widgets-shared/src/constants';
import { Masonry } from './Masonry';
import { type Settings, type ViewerBoardItem, type ViewerAsset } from './types';
import { IMAGE_EXTENSIONS } from './Value';
import { getLabel } from './utils';

type AssetsById = Record<string, ViewerAsset>;

type GridItemProps = {
  row: Row<ViewerBoardItem>;
  settings: Settings;
  assetsById: AssetsById;
  previewTumbnailGeneratorUrl?: string;
};

function GridItem({ row, settings, assetsById, previewTumbnailGeneratorUrl }: GridItemProps) {
  const imageMode = settings.gridItemImageMode || 'thumbnail';
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loadingImageIndex, setLoadingImageIndex] = useState<number | null>(null);
  const loadingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  let cardImage = null;
  let headline = null;

  const startThumbnailLoader = (index: number) => {
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
    }

    loadingTimeoutRef.current = window.setTimeout(() => {
      setLoadingImageIndex(index);
    }, 180);
  };

  const stopThumbnailLoader = () => {
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    setLoadingImageIndex(null);
  };

  const getImageUrl = (asset: any) => {
    if (asset._thumbnails) {
      const thumbnailUrl =
        settings.gridItemImageColumnFormat && asset._thumbnails[settings.gridItemImageColumnFormat]
          ? asset._thumbnails[settings.gridItemImageColumnFormat]
          : asset._thumbnails[DEFAULT_IMAGE_FORMAT_ID];

      return `https://thumbnails.getgorilla.app${thumbnailUrl}`;
    }

    return `${previewTumbnailGeneratorUrl}?mode=${
      settings.gridItemImageColumnFormat || DEFAULT_IMAGE_FORMAT_ID
    }&source=${encodeURIComponent(asset.public_url!)}`;
  };

  const getThumbnailImageUrl = (asset: any) => {
    if (asset._thumbnails && asset._thumbnails['1:1_SMALL']) {
      return `https://thumbnails.getgorilla.app${asset._thumbnails['1:1_SMALL']}`;
    }

    if (previewTumbnailGeneratorUrl) {
      return `${previewTumbnailGeneratorUrl}?mode=1:1_SMALL&source=${encodeURIComponent(asset.public_url!)}`;
    }

    return getImageUrl(asset);
  };

  if (settings.gridItemImageColumn && (row.original.values[settings.gridItemImageColumn]?.value as any)?.asset_ids?.length) {
    const assetIds: string[] = (row.original.values[settings.gridItemImageColumn]?.value as any)?.asset_ids || [];

    const imageAssetIds = assetIds.filter((assetId) => {
      const asset = assetsById[assetId];

      if (!asset) {
        return false;
      }

      return IMAGE_EXTENSIONS.includes(asset.extension);
    });

    if (imageMode === 'thumbnail') {
      const imageAssetId = imageAssetIds[0];

      if (imageAssetId) {
        const asset = assetsById[imageAssetId];

        cardImage = (
          <div className="card-image" data-format={settings.gridItemImageColumnFormat || '16:9_LARGE'}>
            <img src={getImageUrl(asset)} width="" height="" alt="" />
          </div>
        );
      }
    }

    if (imageMode === 'gallery' && imageAssetIds.length) {
      const selectedImageAssetId = imageAssetIds[selectedImageIndex] || imageAssetIds[0];
      const selectedAsset = assetsById[selectedImageAssetId];
      const hasMultipleImages = imageAssetIds.length > 1;

      const selectImage = (index: number) => {
        if (index === selectedImageIndex) {
          return;
        }

        startThumbnailLoader(index);
        setSelectedImageIndex(index);
      };

      const goToPreviousImage = () => {
        const previousIndex = selectedImageIndex === 0 ? imageAssetIds.length - 1 : selectedImageIndex - 1;
        selectImage(previousIndex);
      };

      const goToNextImage = () => {
        const nextIndex = selectedImageIndex === imageAssetIds.length - 1 ? 0 : selectedImageIndex + 1;
        selectImage(nextIndex);
      };

      cardImage = (
        <div className="card-gallery">
          <div className="card-image" data-format={settings.gridItemImageColumnFormat || '16:9_LARGE'}>
            <img
              key={selectedImageAssetId}
              src={getImageUrl(selectedAsset)}
              width=""
              height=""
              alt=""
              onLoad={stopThumbnailLoader}
              onError={stopThumbnailLoader}
            />

            {hasMultipleImages && (
              <>
                <button type="button" className="gallery-arrow gallery-arrow-prev" onClick={goToPreviousImage} aria-label="Previous image">
                  <i className="fa-solid fa-chevron-left"></i>
                </button>

                <button type="button" className="gallery-arrow gallery-arrow-next" onClick={goToNextImage} aria-label="Next image">
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </>
            )}
          </div>

          {hasMultipleImages && (
            <div className="card-thumbnails">
              {imageAssetIds.map((assetId, index) => {
                const asset = assetsById[assetId];

                return (
                  <button
                    key={assetId}
                    type="button"
                    className={index === selectedImageIndex ? 'thumbnail active' : 'thumbnail'}
                    onClick={() => selectImage(index)}
                  >
                    <img
                      src={getThumbnailImageUrl(asset)}
                      width="30"
                      height="30"
                      alt=""
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    />

                    {loadingImageIndex === index && (
                      <span className="thumbnail-loader">
                        <i className="fa-solid fa-spinner-scale fa-spin-pulse"></i>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }
  }

  if (settings.gridItemImageColumn && !cardImage) {
    cardImage = (
      <div className="SkeletonImage" data-format={settings.gridItemImageColumnFormat}>
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-image"></i>
          <p style={{ marginTop: '20px', fontSize: '14px' }}>{getLabel('no_image', settings)}</p>
        </div>
      </div>
    );
  }

  if (settings.gridItemHeadlineColumn) {
    let headlineText = null;

    if (settings.gridItemHeadlineColumn === 'name') {
      headlineText = row.original.name;
    } else if ((row.original.values[settings.gridItemHeadlineColumn]?.value as any).text) {
      headlineText = (row.original.values[settings.gridItemHeadlineColumn]?.value as any).text;
    }

    if (headlineText) {
      headline = (
        <div className="card-headline">
          <h2>{headlineText}</h2>
        </div>
      );
    }
  }

  return (
    <div className="grid--item">
      {cardImage}
      <div className="card-content">
        {headline}
        {row.getVisibleCells().map((cell) => (
          <div key={cell.id} className="value-wrapper">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ))}
      </div>
    </div>
  );
}

type GridProps = {
  mode: 'preview' | 'publish';
  settings: Settings;
  assetsById: AssetsById;
  noBoardItems: boolean;
  columns: ColumnDef<ViewerBoardItem, string>[];
  previewTumbnailGeneratorUrl?: string;
  table: Table<ViewerBoardItem>;
};

export function Grid({ table, settings, assetsById, previewTumbnailGeneratorUrl, mode, noBoardItems }: GridProps) {
  return (
    <div className={`grid content-alignment-${settings.textAlignment}`}>
      {table.getRowModel().rows.length === 0 ? (
        <div style={{ paddingBottom: '20px', textAlign: 'left' }}>
          {getLabel(noBoardItems ? 'nothing_to_show' : 'no_results_found', settings)}
        </div>
      ) : (
        <Masonry
          mode={settings.gridMode ? settings.gridMode : 'grid'}
          items={table.getRowModel().rows}
          config={{
            columns: [1, 2, 3, 4],
            media: [0, 600, 900, 1200],
          }}
          getWindow={() => {
            try {
              if (mode === 'preview') {
                return (window.document.querySelector('#viewer-iframe') as any).contentWindow;
              } else {
                return window;
              }
            } catch (e) {
              return null;
            }
          }}
          render={(row) => (
            <GridItem
              key={row.original.id}
              row={row}
              settings={settings}
              assetsById={assetsById}
              previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
            />
          )}
        />
      )}
    </div>
  );
}
